import { useState, type ReactNode } from 'react';
import { usePermission } from '../../auth/AuthContext';
import { AuditLogDetailModal } from '../../features/administration/audit-logs/components/AuditLogDetailModal';
import { formatDateTime, toIso } from '../../lib/formatDate';
import { DateCell } from './DateCell';
import { StackedCell } from './StackedCell';

/**
 * `Latest update`, as a TABLE cell and as a LIST row's stamp — and a click through to the
 * audit entry behind it.
 *
 * **The stamp said WHEN and WHO and never WHAT** (27 Oct 2026). A reader looking at
 * *Oct 27, 2026, 02:56 PM · Harris Pogi* has exactly one follow-up — what did they change —
 * and answering it meant the row kebab, History Logs, finding the row again and opening it.
 * The cell is where the question is asked, so it is where it is answered.
 *
 * **It opens the SAME `<AuditLogDetailModal>` History Logs does**, naming the entry by the
 * record and the instant rather than by an id a table row has no way to know. One modal, so
 * the field-level before/after cannot come to mean two depths of the same thing.
 *
 * **BOTH SURFACES MOVE TOGETHER**, which is why they share this file: the table draws the
 * stamp as a column and the list draws it under the status badge, and a list whose stamp did
 * nothing would be the two views silently differing — the defect the shared `columns` exist
 * to prevent.
 */

/**
 * The gate, the modal and the open state — the three parts both surfaces need and neither
 * should own a second copy of.
 *
 * Three things decide whether the stamp is a button at all, and failing any of them renders
 * exactly what rendered before:
 *
 * - **`audit-logs.read`.** A control carries the key of what it DOES, not the key of the page
 *   it sits on, and what this one does is read the audit trail. Hidden, not disabled — a pure
 *   permission gate hides.
 * - **An instant.** A record never touched since it was created has no update, so there is
 *   nothing to open.
 * - **A record id.** The shell passes `rowKey`, which is `row.id` at all but two call sites;
 *   where it is something else the server finds no entry and the modal says so.
 */
function useAuditTrigger(entityId: string | null, at: string | null) {
  const canOpen = usePermission('audit-logs.read');
  const [open, setOpen] = useState(false);

  const enabled = canOpen && entityId != null && at != null;

  return {
    enabled,
    openIt: () => setOpen(true),
    // Mounted only while open — a page of rows must not hold fifty modals.
    modal:
      enabled && open ? (
        <AuditLogDetailModal
          id={null}
          latestUpdate={{ entityId: entityId as string, at: at as string }}
          onClose={() => setOpen(false)}
        />
      ) : null,
  };
}

/** The shared `<button>`, so the stop-propagation and the hover underline are written once. */
function TriggerButton({
  className,
  onClick,
  children,
}: {
  className: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={className}
      onClick={(event) => {
        // The row's own open handler sits on the anchor; this cell is not it.
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
      <span className="sr-only">Show what changed in this update</span>
    </button>
  );
}

/** The TABLE cell — the instant over its actor. */
export function LatestUpdateCell({
  entityId,
  updatedAt,
  updatedByName,
}: {
  /** The record the row stands for — the shell's own `rowKey`. */
  entityId: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}) {
  const { enabled, openIt, modal } = useAuditTrigger(entityId, updatedAt);

  /*
    BLANK, never an em dash — a record never touched since it was created has no update, and
    a dash would claim one that did not happen. `Created` takes the dash, because every record
    has a creation and a missing one is a fault. With no instant there is no actor either, so
    `<StackedCell>` drops the second line on its own and the cell is one blank.
  */
  const stamp = (
    <StackedCell
      primary={<DateCell value={updatedAt} fallback="" interactive={enabled} />}
      secondary={updatedAt != null ? updatedByName : null}
      mono
      approvedDeviation
    />
  );

  if (!enabled) {
    return stamp;
  }

  return (
    <>
      {/*
        The whole stamp is the target, both lines of it — the instant and the actor are ONE
        fact, which is the argument that bought this stacked cell its exception in the first
        place, and splitting the click would contradict it.
      */}
      <TriggerButton className="block w-full min-w-0 text-left hover:underline" onClick={openIt}>
        {stamp}
      </TriggerButton>

      {modal}
    </>
  );
}

/**
 * The LIST row's stamp — `Latest update · 27 Oct 2026, 14:56`, muted, under the badge.
 *
 * It renders NOTHING on a record never touched since creation: not a sentence saying so, and
 * not an empty element either, which would hold a line of height the badge above then floats
 * over. The absence is the statement, exactly as the table's blank cell is.
 *
 * **No actor here, and that is not an omission** — the list row already opens with who made
 * the record, and a second name on the same row is the one value printed twice.
 */
export function LatestUpdateStamp({
  entityId,
  updatedAt,
}: {
  entityId: string | null;
  updatedAt: string | null;
}) {
  const { enabled, openIt, modal } = useAuditTrigger(entityId, updatedAt);

  if (updatedAt == null) {
    return null;
  }

  const body = (
    <>
      Latest update
      <span aria-hidden="true" className="px-1 text-slate-300">
        ·
      </span>
      {/* Dates stay monospaced even inline — reference §4. */}
      <time dateTime={toIso(updatedAt)} className="font-mono tabular-nums">
        {formatDateTime(updatedAt)}
      </time>
    </>
  );

  if (!enabled) {
    return <p className="mt-1 truncate text-xs text-slate-400">{body}</p>;
  }

  /*
    A <button> rather than the <p>, because a <p> inside a button is not phrasing content.
    The classes are the paragraph's, so the line does not move when a reader gains the key —
    the tone stays MUTED too: this sits beside the status badge, and the clickable slate-800
    would make a secondary stamp the heaviest thing on the row.
  */
  return (
    <>
      <TriggerButton
        className="mt-1 block max-w-full truncate text-left text-xs text-slate-400 hover:underline"
        onClick={openIt}
      >
        {body}
      </TriggerButton>

      {modal}
    </>
  );
}
